import { Button, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { combineLatest, map } from "rxjs";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { createListStore } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { keyBy } from "lodash";
import { createAxios } from "../../common/axios";
import { RetentionSaveDto } from "./types";

export function RetentionDialog(props: {
    orderId: number;
    close: () => void;
    onRetentionSelected(data: RetentionSaveDto): Promise<boolean>;
}) {
    const [{ useCancelReasonsStore, useRetentionCodesStore }] = useState(() => {
        const useRetentionListsStore = createEntityStore<RetentionListsDto>({
            path: "customers/retentions/getLists", data: emptyLists
        });

        const useCancelReasonsStore = createListStore({
            data: useRetentionListsStore.getState().clientData$.pipe(map(p => ({ items: p.data.cancelReasons, isFetching: p.isFetching }))),
            listProps: [
                { label: "ID", column: "id", width: 50, type: "number" },
                { label: "Text", column: "rsnTxt", width: 360 },
            ]
        });

        const useRetentionCodesStore = createListStore({
            data: combineLatest([
                useRetentionListsStore.getState().clientData$,
                useCancelReasonsStore.getState().selectedItem$,
            ]).pipe(map(([lists, cancelReason]) => {
                if (cancelReason == null) {
                    return { items: [], isFetching: lists.isFetching };
                }
                const retentionsDict = keyBy(lists.data.retentions.filter(p => p.cancelReasonId === cancelReason.id), p => p.returnCodeId);
                return {
                    items: cancelReason == null ? [] : lists.data.retentionCodes.filter(p => retentionsDict[p.id] != null),
                    isFetching: lists.isFetching
                };
            })),
            listProps: [
                { label: "ID", column: "id", width: 50, type: "number" },
                { label: "Text", column: "rtnTxt", width: 360 },
            ]
        });

        return { useCancelReasonsStore, useRetentionCodesStore };
    });

    const cancelReason = useCancelReasonsStore(p => p.selectedItem);
    const retentionCode = useRetentionCodesStore(p => p.selectedItem);

    const [isSaving, setIsSaving] = useState(false);
    async function save() {
        setIsSaving(true);
        const data: RetentionSaveDto = { cancelReasonId: cancelReason!.id, retentionCodeId: retentionCode!.id };
        try {
            if (await props.onRetentionSelected(data)) {
                props.close();
            } else {
                await createAxios().post("customers/retentions", { ...data, orderId: props.orderId });
                props.close();
            }
        } finally {
            setIsSaving(false);
        }
    }

    return <>
        <DialogTitle>Retentions</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4}>
                <Grid item xs={6}>
                    <Typography variant="h6">Cancelation reasons</Typography>
                    {renderListStore(useCancelReasonsStore)}
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6">Retention codes</Typography>
                    {renderListStore(useRetentionCodesStore)}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={save} disabled={isSaving || cancelReason == null || retentionCode == null} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

const emptyLists: RetentionListsDto = {
    cancelReasons: [],
    retentionCodes: [],
    retentions: [],
}

interface RetentionListsDto {
    cancelReasons: CancelReasonDto[];
    retentionCodes: RetentionCodeDto[];
    retentions: RetentionLinkDto[];
}

interface CancelReasonDto {
    id: number;
    rsnCde: string;
    rsnTxt: string;
}

interface RetentionCodeDto {
    id: number;
    rtnCde: string;
    rtnTxt: string;
}

interface RetentionLinkDto {
    id: number;
    cancelReasonId: number;
    returnCodeId: number;
}
